'use client';

import Image from 'next/image';
import Link from 'next/link';
import useEmblaCarousel from 'embla-carousel-react';
import { EmblaOptionsType } from 'embla-carousel';

import { DotButton, useDotButton } from '@/components/search/carousel/EmblaCarouselDotButton';
import { NextButton, PrevButton, usePrevNextButtons } from '@/components/search/carousel/EmblaCarouselArrowButton';
import '@/components/search/carousel/css/carousel.css';
import './safari-countries-carousel.css';

const S3_BASE = 'https://faunaexplore-places.s3.eu-central-1.amazonaws.com';
const CDN_BASE = 'https://d2qyr742jfkcpm.cloudfront.net';

const SAFARI_COUNTRIES = [
  {
    slug: 'norway',
    name: 'Norway',
    image: `${CDN_BASE}/landing-page/experience/orca-expedition/orcas-bellow-surface.jpg`,
    alt: 'Orcas swimming beneath the surface in Norway',
  },
  {
    slug: 'chile',
    name: 'Chile',
    image: '/static/images/puma-head-peeking-form-vegetation-mountains-in-landscape.jpg',
    alt: 'Puma in the Chilean Patagonia landscape',
  },
  {
    slug: 'india',
    name: 'India',
    image: `${S3_BASE}/asia/india/tiger-intense-eye-contact.jpg`,
    alt: 'Tiger on a wildlife safari in India',
  },
  {
    slug: 'indonesia',
    name: 'Indonesia',
    image: `${S3_BASE}/asia/indonesia/mola-mola-fish-with-divers-in-background.jpg`,
    alt: 'Mola mola fish with divers in Indonesia',
  },
  {
    slug: 'kenya',
    name: 'Kenya',
    image: `${S3_BASE}/kenya/kenya-lion-sunset.jpg`,
    alt: 'Lion on a wildlife safari in Kenya',
  },
  {
    slug: 'tanzania',
    name: 'Tanzania',
    image: `${S3_BASE}/tanzania/1-safari-great-migration.jpg`,
    alt: 'Wildlife during the Great Migration in Tanzania',
  },
  {
    slug: 'south-africa',
    name: 'South Africa',
    image: `${S3_BASE}/south-africa/leopard.jpg`,
    alt: 'Leopard on safari in South Africa',
  },
  {
    slug: 'uganda',
    name: 'Uganda',
    image: `${S3_BASE}/uganda/uganda.jpg`,
    alt: 'Wildlife in Uganda',
  },
] as const;

const OPTIONS: EmblaOptionsType = {
  align: 'start',
  loop: false,
  containScroll: 'trimSnaps',
};

export function SafariCountriesSection() {
  const [emblaRef, emblaApi] = useEmblaCarousel(OPTIONS);
  const { selectedIndex, scrollSnaps, onDotButtonClick } = useDotButton(emblaApi);
  const { prevBtnDisabled, nextBtnDisabled, onPrevButtonClick, onNextButtonClick } = usePrevNextButtons(emblaApi);

  return (
    <section
      className="-mt-12 lg:-mt-16 scroll-mt-24"
      aria-label="Explore safari by country"
    >
      <div className="section-title carousel-title font-medium mb-2">
        <h2 role="title" className="inline">
          Explore by country.
        </h2>
        <span className="h2">
          {' '}
          <span className="text-dimmer">Discover the world&apos;s top wildlife destinations.</span>
        </span>
      </div>

      <div className="safari-countries-embla embla mt-auto lg:-mt-12">
        <div className="embla__controls">
          <div className="embla__buttons">
            <PrevButton onClick={onPrevButtonClick} disabled={prevBtnDisabled} aria-label="Previous country" />
            <NextButton onClick={onNextButtonClick} disabled={nextBtnDisabled} aria-label="Next country" />
          </div>
        </div>

        <div className="embla__viewport-wrapper relative">
          <div className="embla__viewport" ref={emblaRef}>
            <div className="embla__container safari-countries-container">
              {SAFARI_COUNTRIES.map(({ slug, name, image, alt }) => (
                <div className="embla__slide safari-countries-slide" key={slug}>
                  <Link
                    href={`/destinations/${slug}`}
                    className="group relative block aspect-[4/3] sm:aspect-[3/2] overflow-hidden rounded-lg border border-gray-700/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-950"
                  >
                    <Image
                      src={image}
                      alt={alt}
                      fill
                      loading="lazy"
                      sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 20vw"
                      className="object-cover transition-transform duration-300 group-hover:scale-105"
                      draggable={false}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                    <span className="absolute bottom-0 left-0 right-0 p-3 sm:p-4 text-white font-semibold text-sm sm:text-base drop-shadow-md">
                      {name}
                    </span>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="embla__dots">
          {scrollSnaps.map((_, index) => (
            <DotButton
              key={index}
              onClick={() => onDotButtonClick(index)}
              className={'embla__dot'.concat(index === selectedIndex ? ' embla__dot--selected' : '')}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
